import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, AlertTriangle, Package, Edit, Trash2, Upload, X, Boxes, Search, Check, ChevronsUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { compressImage } from '../../utils/imageCompressor';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { formatAmount } from '../../utils/format';

const emptyForm = {
  product_id: '',
  sku: '',
  cost: 0,
  stock: 0,
  min_stock: 0,
  image_url: '',
  tracked: true
};

const InventoryTab = () => {
  const { getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [variantForms, setVariantForms] = useState([]);
  const [unregisteredOpen, setUnregisteredOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/inventory`, { headers: getAuthHeaders() })
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setInventory(Array.isArray(inventoryRes.data) ? inventoryRes.data : []);
    } catch {
      toast.error('Error al cargar inventario');
    }
  };

  // Productos sin registro de inventario (para el selector de alta)
  const registeredIds = new Set(inventory.map((it) => it.id));
  const unregisteredProducts = products.filter((p) => !registeredIds.has(p.id));

  const filteredInventory = inventory.filter((it) => {
    const name = (it.product_name || '').toLowerCase();
    return !search.trim() || name.includes(search.trim().toLowerCase()) || (it.sku || '').toLowerCase().includes(search.trim().toLowerCase());
  });

  // ===== Utilidades de inventario por variante =====
  // Slots de variante de un doc de inventario: { variant_id, variant_name, cost, stock, min_stock, tracked }
  const itemVariantSlots = (item) => {
    const v = item && item.variants;
    if (!v || typeof v !== 'object') return [];
    return Object.entries(v).map(([vid, slot]) => ({ variant_id: vid, ...(slot || {}) }));
  };
  const isVariantTracked = (item) => itemVariantSlots(item).length > 0;
  const aggregateStock = (item) => itemVariantSlots(item)
    .filter((s) => s.tracked !== false)
    .reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
  const displayStock = (item) => (isVariantTracked(item) ? aggregateStock(item) : (Number(item.stock) || 0));
  const isItemLow = (item) => {
    const slots = itemVariantSlots(item);
    if (slots.length > 0) {
      return slots.some((s) => s.tracked !== false && Number(s.stock) <= Number(s.min_stock || 0));
    }
    return item.tracked !== false && Number(item.stock) <= Number(item.min_stock);
  };
  const productHasVariants = (productId) => {
    const prod = products.find((p) => p.id === productId);
    return (prod && Array.isArray(prod.variants) ? prod.variants.filter((v) => v && v.id) : []).length > 0;
  };
  // Formularios por variante del diálogo: parte de las variantes del catálogo y
  // rellena con los slots ya guardados en el doc de inventario (si existen).
  const buildVariantForms = (productId, slots = {}) => {
    const prod = products.find((p) => p.id === productId);
    const vs = (prod && Array.isArray(prod.variants) ? prod.variants.filter((v) => v && v.id) : []);
    return vs.map((v) => {
      const slot = slots[v.id] || {};
      return {
        variant_id: v.id,
        variant_name: v.name,
        cost: slot.cost != null ? slot.cost : (v.cost != null ? v.cost : 0),
        stock: slot.stock != null ? slot.stock : 0,
        min_stock: slot.min_stock != null ? slot.min_stock : 0
      };
    });
  };
  const updateVariantForm = (index, field, value) => {
    setVariantForms((prev) => prev.map((vf, idx) => (idx === index ? { ...vf, [field]: value } : vf)));
  };
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const lowStockCount = inventory.filter((it) => isItemLow(it)).length;

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressedFile = await compressImage(file);
    setImageFile(compressedFile);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(compressedFile);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setVariantForms([]);
    setImageFile(null);
    setImagePreview(null);
    setIsDialogOpen(true);
  };

  // Abre el diálogo de alta con el producto ya seleccionado (desde el listado "sin inventario")
  const handleRegisterProduct = (product) => {
    setEditingItem(null);
    setFormData({ ...emptyForm, product_id: product.id });
    setVariantForms(productHasVariants(product.id) ? buildVariantForms(product.id, {}) : []);
    setImageFile(null);
    setImagePreview(product.image_url || null);
    setUnregisteredOpen(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      product_id: item.id,
      sku: item.sku || '',
      cost: item.cost || 0,
      stock: item.stock || 0,
      min_stock: item.min_stock || 0,
      image_url: item.image_url || '',
      tracked: item.tracked !== false
    });
    setVariantForms(productHasVariants(item.id) ? buildVariantForms(item.id, item.variants || {}) : []);
    setImageFile(null);
    setImagePreview(item.image_url || item.product_image_url || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingItem(null);
      setFormData(emptyForm);
      setVariantForms([]);
      setImageFile(null);
      setImagePreview(null);
    }, 100);
  };

  // Precios de venta del producto (base + variantes) para compararlos con el costo
  const getProductSellingPrices = (productId) => {
    const prod = products.find((p) => p.id === productId);
    if (!prod) return [];
    return [Number(prod.price), ...(prod.variants || []).map((v) => Number(v.price))]
      .filter((pr) => Number.isFinite(pr) && pr >= 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && !formData.product_id) {
      toast.error('Selecciona un producto');
      return;
    }
    const targetProductId = editingItem ? editingItem.id : formData.product_id;
    const variantMode = productHasVariants(targetProductId);
    if (variantMode && variantForms.length === 0) {
      toast.error('El producto tiene variantes pero no se pudo cargar ninguna');
      return;
    }
    setLoading(true);

    try {
      let finalImageUrl = formData.image_url;
      if (imageFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/inventory/upload-image`, uploadForm, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        finalImageUrl = uploadRes.data.image_url;
      }

      if (variantMode) {
        // El control por variante retira la existencia base: solo se gestionan los slots.
        // Si no hay doc base, se crea (para conservar sku/imagen); si existe, se actualiza imagen/sku.
        if (!editingItem) {
          await axios.post(`${API}/inventory`, {
            product_id: targetProductId,
            sku: formData.sku || null,
            cost: 0,
            stock: 0,
            min_stock: 0,
            image_url: finalImageUrl,
            tracked: true
          }, { headers: getAuthHeaders() });
        } else if (finalImageUrl || formData.sku) {
          await axios.put(`${API}/inventory/${targetProductId}`, {
            sku: formData.sku || null,
            image_url: finalImageUrl
          }, { headers: getAuthHeaders() });
        }
        for (const vf of variantForms) {
          await axios.put(`${API}/inventory/${targetProductId}/variants/${vf.variant_id}`, {
            variant_name: vf.variant_name,
            cost: Number(vf.cost) || 0,
            stock: Number(vf.stock) || 0,
            min_stock: Number(vf.min_stock) || 0,
            tracked: true
          }, { headers: getAuthHeaders() });
        }
        toast.success(editingItem ? 'Inventario por variante actualizado' : 'Inventario por variante registrado');
      } else {
        const payload = {
          sku: formData.sku || null,
          cost: Number(formData.cost) || 0,
          stock: Number(formData.stock) || 0,
          min_stock: Number(formData.min_stock) || 0,
          image_url: finalImageUrl,
          tracked: formData.tracked
        };

        if (editingItem) {
          await axios.put(`${API}/inventory/${editingItem.id}`, payload, { headers: getAuthHeaders() });
          toast.success('Inventario actualizado');
        } else {
          await axios.post(`${API}/inventory`, { ...payload, product_id: formData.product_id }, { headers: getAuthHeaders() });
          toast.success('Inventario registrado');
        }

        // Aviso: el costo guardado queda por encima de algún precio de venta del producto.
        if (payload.cost > 0 && getProductSellingPrices(targetProductId).some((pr) => pr < payload.cost)) {
          toast.warning(`El costo ($${formatAmount(payload.cost)}) queda por encima de algún precio de venta. Ajusta los precios del producto.`);
        }
      }

      await loadData();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar inventario'));
    }

    setLoading(false);
  };

  const handleDeleteVariant = async (item, variantId, variantName) => {
    if (!window.confirm(`¿Quitar el control de inventario de la variante "${variantName}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/inventory/${item.id}/variants/${variantId}`, { headers: getAuthHeaders() });
      toast.success('Inventario de variante eliminado');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar la variante'));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Quitar el control de inventario de "${item.product_name || item.id}"? El producto volverá a venderse sin restricciones de stock.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/inventory/${item.id}`, { headers: getAuthHeaders() });
      toast.success('Registro de inventario eliminado');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar'));
    }
  };

  const displayImage = (item) => item.image_url || item.product_image_url || null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Inventario de Productos</h2>
          <p className="text-sm text-gray-500">Costo y existencia de los artículos que se venden en el POS</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(v) => (v ? handleOpenCreate() : handleCloseDialog())}>
          <DialogTrigger asChild>
            <Button
              onClick={handleOpenCreate}
              data-testid="create-inventory-button"
              className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Registrar Inventario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Inventario' : 'Registrar Inventario'}</DialogTitle>
              <DialogDescription>
                {editingItem
                  ? 'Modifica el costo, la existencia y la imagen del artículo'
                  : 'Vincula un producto existente con su costo y existencia'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingItem && (
                <div>
                  <Label>Producto</Label>
                  <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={productPopoverOpen}
                        data-testid="inventory-product-combobox"
                        className="w-full justify-between rounded-xl border-gray-300 px-3 py-2 text-sm font-normal bg-white hover:bg-white"
                      >
                        <span className="truncate text-left">
                          {unregisteredProducts.find((p) => p.id === formData.product_id)?.name || 'Selecciona un producto...'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar producto..." className="h-9" />
                        <CommandList>
                          <CommandEmpty>
                            {unregisteredProducts.length === 0
                              ? 'Todos los productos ya tienen inventario.'
                              : 'Sin productos coincidentes.'}
                          </CommandEmpty>
                          <CommandGroup>
                            {unregisteredProducts.map((p) => (
                              <CommandItem
                                key={p.id}
                                value={p.name}
                                onSelect={() => {
                                  setFormData({ ...formData, product_id: p.id });
                                  setVariantForms(buildVariantForms(p.id, {}));
                                  setProductPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${formData.product_id === p.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <span className="truncate">{p.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {unregisteredProducts.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Todos los productos ya tienen inventario registrado.</p>
                  )}
                </div>
              )}
              {editingItem && (
                <div>
                  <Label>Producto</Label>
                  <Input value={editingItem.product_name || editingItem.id} disabled className="rounded-xl" />
                </div>
              )}
              <div>
                <Label>SKU (opcional)</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="Código de referencia"
                  className="rounded-xl"
                />
              </div>
              {productHasVariants(editingItem ? editingItem.id : formData.product_id) ? (
                <div className="space-y-3">
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    Este producto tiene variantes: la existencia se controla por variante y se retira la existencia base del producto. Captura un conteo inicial o registra una compra para cada variante.
                  </div>
                  {variantForms.map((vf, idx) => (
                    <div key={vf.variant_id} className="border border-orange-100 rounded-xl p-3 bg-orange-50/30 space-y-2">
                      <p className="text-sm font-semibold text-gray-800">{vf.variant_name}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Costo ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={vf.cost}
                            onChange={(e) => updateVariantForm(idx, 'cost', parseFloat(e.target.value) || 0)}
                            className="rounded-xl h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Existencia</Label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={vf.stock}
                            onChange={(e) => updateVariantForm(idx, 'stock', parseInt(e.target.value, 10) || 0)}
                            className="rounded-xl h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Mínimo (alerta)</Label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={vf.min_stock}
                            onChange={(e) => updateVariantForm(idx, 'min_stock', parseInt(e.target.value, 10) || 0)}
                            className="rounded-xl h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Costo ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.cost}
                        onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Existencia</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value, 10) || 0 })}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Mínimo (alerta)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.min_stock}
                        onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value, 10) || 0 })}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.tracked}
                      onChange={(e) => setFormData({ ...formData, tracked: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    Controlar existencia (ocultar en POS y validar al vender)
                  </label>
                </>
              )}
              <div>
                <Label>Imagen del artículo (opcional)</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/*" onChange={handleImageChange} className="rounded-xl" id="inventory-image-upload" />
                    <Label htmlFor="inventory-image-upload" className="cursor-pointer">
                      <div className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl hover:bg-orange-200 transition-colors flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Subir
                      </div>
                    </Label>
                  </div>
                  {(imagePreview || formData.image_url) && (
                    <div className="relative w-full h-40 bg-gray-100 rounded-xl border-2 border-orange-200 overflow-hidden">
                      <img
                        src={imagePreview || getImageUrl(formData.image_url)}
                        alt="Preview"
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {imagePreview && (
                        <div className="absolute top-2 right-2">
                          <Button type="button" size="sm" variant="destructive" onClick={() => { setImageFile(null); setImagePreview(null); }} className="rounded-full w-8 h-8 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                {loading ? 'Guardando...' : (editingItem ? 'Actualizar' : 'Registrar')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lowStockCount > 0 && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">{lowStockCount} artículo(s) con existencia baja o agotada</span>
          </div>
        </Card>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por producto o SKU..."
          className="pl-9 rounded-xl"
        />
      </div>

      <Card className="overflow-hidden bg-white/80 backdrop-blur-xl border-orange-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Artículo</th>
                <th className="px-4 py-3 text-left font-semibold">SKU</th>
                <th className="px-4 py-3 text-right font-semibold">Costo</th>
                <th className="px-4 py-3 text-right font-semibold">Existencia</th>
                <th className="px-4 py-3 text-right font-semibold">Mínimo</th>
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => {
                const vSlots = itemVariantSlots(item);
                const hasVar = vSlots.length > 0;
                const stock = displayStock(item);
                const isLow = isItemLow(item);
                const isOut = (hasVar || item.tracked !== false) && Number(stock) <= 0;
                const minStock = hasVar ? vSlots.reduce((s, v) => s + (Number(v.min_stock) || 0), 0) : item.min_stock;
                const expanded = expandedIds.includes(item.id);
                const img = displayImage(item);
                return (
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-orange-50 ${isLow ? 'bg-amber-50' : 'hover:bg-orange-50/40'}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {hasVar && (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(item.id)}
                              data-testid={`toggle-variants-${item.id}`}
                              className="text-orange-500 hover:text-orange-700 flex-shrink-0"
                              title={expanded ? 'Ocultar variantes' : 'Ver variantes'}
                            >
                              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          )}
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center flex-shrink-0">
                            {img ? (
                              <img src={getImageUrl(img)} alt={item.product_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <Package className="w-5 h-5 text-orange-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-800">{item.product_name || item.id}</p>
                              {hasVar && (
                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] px-1.5 py-0">
                                  por variante
                                </Badge>
                              )}
                            </div>
                            {item.product_price != null && (
                              <p className="text-xs text-gray-500">Venta: ${formatAmount(item.product_price)}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.sku || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-orange-600">{hasVar ? '—' : `$${formatAmount(item.cost || 0)}`}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-green-600'}`}>
                          {stock}
                        </span>
                        {isOut && <span className="ml-2 text-xs text-red-600">(agotado)</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{minStock}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <Button onClick={() => handleEdit(item)} variant="outline" size="sm" data-testid={`edit-inventory-${item.id}`} className="border-orange-200 hover:bg-orange-50 rounded-xl">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => handleDelete(item)} variant="outline" size="sm" data-testid={`delete-inventory-${item.id}`} className="border-red-200 hover:bg-red-50 text-red-600 rounded-xl">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {hasVar && expanded && vSlots.map((slot) => {
                      const sLow = slot.tracked !== false && Number(slot.stock) <= Number(slot.min_stock || 0);
                      const sOut = slot.tracked !== false && Number(slot.stock) <= 0;
                      return (
                        <tr key={`${item.id}-${slot.variant_id}`} className="border-b border-orange-50 bg-purple-50/30">
                          <td className="px-4 py-2">
                            <p className="text-sm text-gray-700 pl-11 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block" />
                              {slot.variant_name || slot.variant_id}
                            </p>
                          </td>
                          <td className="px-4 py-2 text-gray-500">{slot.sku || '—'}</td>
                          <td className="px-4 py-2 text-right text-orange-600 font-medium">${formatAmount(slot.cost || 0)}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={`font-semibold ${sOut ? 'text-red-600' : sLow ? 'text-amber-600' : 'text-green-600'}`}>
                              {Number(slot.stock) || 0}
                            </span>
                            {sOut && <span className="ml-2 text-xs text-red-600">(agotada)</span>}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-500">{Number(slot.min_stock) || 0}</td>
                          <td className="px-4 py-2">
                            <div className="flex justify-center">
                              <Button
                                onClick={() => handleDeleteVariant(item, slot.variant_id, slot.variant_name || slot.variant_id)}
                                variant="outline"
                                size="sm"
                                data-testid={`delete-variant-${item.id}-${slot.variant_id}`}
                                className="border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
                                title="Quitar control de esta variante"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredInventory.length === 0 && (
          <div className="text-center py-12">
            <Boxes className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {inventory.length === 0 ? 'No hay artículos con inventario registrado' : 'Ningún artículo coincide con la búsqueda'}
            </p>
            {inventory.length === 0 && (
              <Button onClick={handleOpenCreate} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Registrar el primero
              </Button>
            )}
          </div>
        )}
      </Card>

      {unregisteredProducts.length > 0 && (
        <>
          <Card
            className="p-4 bg-blue-50 border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => setUnregisteredOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setUnregisteredOpen(true);
              }
            }}
          >
            <div className="flex items-center justify-between gap-2 text-blue-700">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">
                  {unregisteredProducts.length} producto(s) sin inventario (se venden sin control de existencia)
                </span>
              </div>
              <span className="flex items-center gap-1 text-xs font-semibold text-blue-600 whitespace-nowrap">
                Ver listado <ChevronRight className="w-4 h-4" />
              </span>
            </div>
          </Card>

          <Dialog open={unregisteredOpen} onOpenChange={setUnregisteredOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-blue-600" />
                  Productos sin inventario ({unregisteredProducts.length})
                </DialogTitle>
                <DialogDescription>
                  Estos productos se venden sin control de existencia. Pulsa «Registrar» para gestionar su stock.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                {unregisteredProducts.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-orange-50/40"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center flex-shrink-0">
                        {p.image_url ? (
                          <img
                            src={getImageUrl(p.image_url)}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Package className="w-5 h-5 text-orange-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800 truncate">{p.name}</p>
                          {productHasVariants(p.id) && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] px-1.5 py-0">
                              por variante
                            </Badge>
                          )}
                        </div>
                        {p.price != null && (
                          <p className="text-xs text-gray-500">Venta: ${formatAmount(p.price)}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRegisterProduct(p)}
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-1 border-orange-200 hover:bg-orange-50 rounded-xl"
                    >
                      <Plus className="w-4 h-4" /> Registrar
                    </Button>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default InventoryTab;
